import {
  type CreateCruiseCabinRateRequest,
  type CreateCruiseCabinRequest,
  type CreateCruiseRequest,
  type CruiseApprovalRequest,
  type CruiseCabin,
  CruiseCabinSchema,
  type CruiseCabinRate,
  CruiseCabinRateSchema,
  type CruiseCabinRateListQuery,
  type CruiseCabinRateListResponse,
  CruiseCabinRateListResponseSchema,
  type CruiseDetail,
  CruiseDetailSchema,
  type CruiseListQuery,
  type CruiseListResponse,
  CruiseListResponseSchema,
  type UpdateCruiseCabinRateRequest,
  type UpdateCruiseCabinRequest,
  type UpdateCruiseRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Cruise master API (Cruise-as-stay phase 1). Every response is validated
 * with the shared Zod schema so a server change can't sneak past the UI
 * types - same convention as `hotels.api.ts`.
 */

export const listCruises = async (query: CruiseListQuery): Promise<CruiseListResponse> => {
  const { data } = await apiClient.get<unknown>('/cruises', { params: query });
  return CruiseListResponseSchema.parse(data);
};

export const getCruise = async (id: string): Promise<CruiseDetail> => {
  const { data } = await apiClient.get<unknown>(`/cruises/${id}`);
  return CruiseDetailSchema.parse(data);
};

export const createCruise = async (payload: CreateCruiseRequest): Promise<CruiseDetail> => {
  const { data } = await apiClient.post<unknown>('/cruises', payload);
  return CruiseDetailSchema.parse(data);
};

export const updateCruise = async (
  id: string,
  payload: UpdateCruiseRequest,
): Promise<CruiseDetail> => {
  const { data } = await apiClient.patch<unknown>(`/cruises/${id}`, payload);
  return CruiseDetailSchema.parse(data);
};

export const approveCruise = async (
  id: string,
  payload: CruiseApprovalRequest,
): Promise<CruiseDetail> => {
  const { data } = await apiClient.post<unknown>(`/cruises/${id}/approval`, payload);
  return CruiseDetailSchema.parse(data);
};

export const deleteCruise = async (id: string): Promise<void> => {
  await apiClient.delete(`/cruises/${id}`);
};

export const duplicateCruise = async (id: string): Promise<CruiseDetail> => {
  const { data } = await apiClient.post<unknown>(`/cruises/${id}/duplicate`);
  return CruiseDetailSchema.parse(data);
};

export const bulkDeleteCruises = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/cruises/bulk-delete', { ids });
  return data;
};

// ----- Cabins sub-resource -----

const CruiseCabinListSchema = z.array(CruiseCabinSchema);

export const listCruiseCabins = async (cruiseId: string): Promise<CruiseCabin[]> => {
  const { data } = await apiClient.get<unknown>(`/cruises/${cruiseId}/cabins`);
  return CruiseCabinListSchema.parse(data);
};

export const createCruiseCabin = async (
  cruiseId: string,
  payload: CreateCruiseCabinRequest,
): Promise<CruiseCabin> => {
  const { data } = await apiClient.post<unknown>(`/cruises/${cruiseId}/cabins`, payload);
  return CruiseCabinSchema.parse(data);
};

export const updateCruiseCabin = async (
  cruiseId: string,
  cabinId: string,
  payload: UpdateCruiseCabinRequest,
): Promise<CruiseCabin> => {
  const { data } = await apiClient.patch<unknown>(
    `/cruises/${cruiseId}/cabins/${cabinId}`,
    payload,
  );
  return CruiseCabinSchema.parse(data);
};

export const deleteCruiseCabin = async (cruiseId: string, cabinId: string): Promise<void> => {
  await apiClient.delete(`/cruises/${cruiseId}/cabins/${cabinId}`);
};

// ----- Rates sub-resource -----

export const listCruiseRates = async (
  cruiseId: string,
  query: CruiseCabinRateListQuery,
): Promise<CruiseCabinRateListResponse> => {
  const { data } = await apiClient.get<unknown>(`/cruises/${cruiseId}/rates`, { params: query });
  return CruiseCabinRateListResponseSchema.parse(data);
};

export const createCruiseRate = async (
  cruiseId: string,
  payload: CreateCruiseCabinRateRequest,
): Promise<CruiseCabinRate> => {
  const { data } = await apiClient.post<unknown>(`/cruises/${cruiseId}/rates`, payload);
  return CruiseCabinRateSchema.parse(data);
};

export const updateCruiseRate = async (
  cruiseId: string,
  rateId: string,
  payload: UpdateCruiseCabinRateRequest,
): Promise<CruiseCabinRate> => {
  const { data } = await apiClient.patch<unknown>(`/cruises/${cruiseId}/rates/${rateId}`, payload);
  return CruiseCabinRateSchema.parse(data);
};

export const deleteCruiseRate = async (cruiseId: string, rateId: string): Promise<void> => {
  await apiClient.delete(`/cruises/${cruiseId}/rates/${rateId}`);
};
